import {useTreePane} from '@github-ui/commits/shared/useTreePane'
import type {DiffUser} from '@github-ui/diff-lines/types'
import {useUpdateUserDiffViewPreferenceMutation} from '@github-ui/diff-view-settings/hooks/mutations/use-update-user-diff-view-preference-mutation'
import {
  useUserDiffViewSettingsData,
  useSplitViewPreference,
  useWhitespacePreference,
} from '@github-ui/diff-view-settings/page-data/payloads/diff-view-settings'
import {CommentsPreference} from '@github-ui/diff-view-settings/types'
import {pullRequestContextLinesPath} from '@github-ui/paths'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {GlobalCommands} from '@github-ui/ui-commands'
import {useHideFooter} from '@github-ui/use-hide-footer'
import {Dialog, SplitPageLayout} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useMemo} from 'react'
import {FileFilter} from '../components/diff-filtering/FileFilter'
import {PR_FILE_TREE_ID, PullRequestFileTree} from '../components/file-tree/PullRequestFileTree'
import treeStyles from '../components/file-tree/PullRequestFileTree.module.css'
import {PageLimitsBanner} from '../components/PageLimitsBanner'
import {PullRequestDiffsList} from '../components/PullRequestDiffsList'
import {PullRequestErrorState} from '../components/PullRequestErrorState'
import {LivePullRequestFilesToolbar} from '../components/toolbar/PullRequestFilesToolbar'
import {SelectedRefContextProvider} from '../contexts/SelectedRefContext'
import {useStitchSubjectIntoThreads} from '../hooks/use-stitch-subject-into-threads'
import {useCodeowners} from '../page-data/loaders/use-codeowners-data'
import type {Codeowners} from '../page-data/payloads/codeowners'
import type {FilesRoutePayload} from '../page-data/payloads/files'
import styles from './Files.module.css'
import {PullRequestStoreProvider} from '../stores/PullRequestStore'
import {useRouteHeaderData} from '../hooks/use-route-header-data'
import {
  useFilteredDiffSummaries,
  useDiffSummaryCount,
  useDiffFilterActions,
  useCodeownersActions,
  useDiffFilterState,
  useUpdateSelectedPathDigest,
} from '../stores/selectors'
import {SingleFileModeDiffEntryNavigator} from '../components/SingleFileModeDiffEntryNavigator'
import type {DiffFilterState} from '../stores/slices/diff-filter'
import type {DiffDelta} from '@github-ui/diff-file-tree/diff-file-tree-helpers'

// entrypoint for JSON route - not DataRouter
export function FilesEntrypoint() {
  return (
    <ErrorBoundary critical fallback={<PullRequestErrorState text="Changes cannot be loaded" />}>
      <Files />
    </ErrorBoundary>
  )
}

export function Files() {
  const routeData = useRoutePayload<FilesRoutePayload>()

  return (
    <SelectedRefContextProvider
      baseRefOid={routeData.pullRequest.comparison.baseOid}
      path={ssrSafeLocation.pathname ?? ''}
    >
      <PullRequestStoreProvider>
        <FilesComponent {...routeData} />
      </PullRequestStoreProvider>
    </SelectedRefContextProvider>
  )
}

export function FilesComponent({
  codeowners,
  commits,
  diffSummaries: initialDiffSummaries,
  fileFilter,
  pullRequest,
  repository,
  markers: initialMarkers,
  pageLimits,
  urls,
  user,
  isSingleFileMode,
}: FilesRoutePayload) {
  // TODO: This is the source of two bugs with suggested changes:
  // - https://github.com/github/pull-requests/issues/18188
  // - https://github.com/github/pull-requests/issues/18360
  // We'll need to migrate this state into Zustand eventually.
  useStitchSubjectIntoThreads(initialMarkers, initialDiffSummaries)

  useHideFooter(true)

  /*
   * Viewer settings
   */
  const viewSettingsData = useViewerSettings(user)

  const {mutate: updateUserDiffViewPreference} = useUpdateUserDiffViewPreferenceMutation({
    onSuccess: () => {},
    onError: () => {},
  })
  const {data: settingsData} = useUserDiffViewSettingsData(viewSettingsData)
  const handleSwapDiffViewCommentCollapsePreference = useCallback(() => {
    updateUserDiffViewPreference({
      commentsPreference:
        settingsData?.commentsPreference === CommentsPreference.Visible
          ? CommentsPreference.Collapsed
          : CommentsPreference.Visible,
    })
  }, [settingsData?.commentsPreference, updateUserDiffViewPreference])

  const currentUser: DiffUser = useMemo(() => {
    return {
      avatarURL: user.currentUserAvatarUrl || '',
      login: user.currentUserLogin || '',
      tabSize: user.tabSize,
      splitPreference: settingsData?.splitPreference ?? 'unified',
      lineSpacing: settingsData?.lineSpacing ?? 'relaxed',
      canComment: user.canComment,
      canApplySuggestion: user.canApplySuggestion,
      commentsPreference: settingsData?.commentsPreference ?? 'visible',
      hasCopilotAccess: user.hasCopilotAccess,
    }
  }, [
    settingsData?.commentsPreference,
    settingsData?.lineSpacing,
    settingsData?.splitPreference,
    user.canApplySuggestion,
    user.canComment,
    user.currentUserAvatarUrl,
    user.currentUserLogin,
    user.hasCopilotAccess,
    user.tabSize,
  ])

  const {
    splitPagePaneHidden,
    splitPageContentHidden,
    isTreeExpanded,
    isMobileTreeExpanded,
    treeToggleElement,
    collapseTree,
  } = useTreePane(PR_FILE_TREE_ID, user.isFileTreeExpanded, currentUser, {
    size: 'small',
    mobileBreakpoint: 'lg',
  })

  /*
   * Diff and Filtering
   */

  // Codeowners data should be set in TSQ before filtering any diff summaries or diff entries,
  // because filtering is dependent on the codeowners data.
  const codeownersOptions: {basePath: string; initialData?: Codeowners} = {basePath: pullRequest.pathName}
  // Only set initialData if it exists
  if (codeowners) codeownersOptions.initialData = codeowners

  // TODO we can likely remove the use of `useCodeowners`. we should determine
  // if we want to use TSQ as the underlying data cache, though.
  const {data: codeownersData} = useCodeowners(codeownersOptions)
  const {setCodeowners} = useCodeownersActions()

  useEffect(() => {
    setCodeowners(codeownersData)
  }, [codeownersData, setCodeowners])

  const diffSummaryCount = useDiffSummaryCount()
  const diffFilterState = useDiffFilterState()
  const {updateDiffFilter} = useDiffFilterActions()

  useEffect(() => {
    updateDiffFilter({
      ...fileFilter.initialState,
      unselectedFileExtensions: new Set<string>(fileFilter.initialState.unselectedFileExtensions),
    })
  }, [updateDiffFilter, fileFilter.initialState])

  const filteredDiffSummaries = useFilteredDiffSummaries()
  const contextLinesURL = useMemo(() => {
    return pullRequestContextLinesPath({
      owner: repository.ownerLogin,
      repo: repository.name,
      number: pullRequest.number,
    })
  }, [repository.name, repository.ownerLogin, pullRequest.number])

  const handleSetFilterState = useCallback(
    (updatedDiffFilterState: Partial<DiffFilterState>) => {
      updateDiffFilter(updatedDiffFilterState, true)
    },
    [updateDiffFilter],
  )
  const updateSelectedPathDigest = useUpdateSelectedPathDigest()

  const handleFileTreeLinkClick = useCallback(
    (file?: DiffDelta) => {
      if (isSingleFileMode && file?.pathDigest) {
        updateSelectedPathDigest(file?.pathDigest)
      }
      if (isMobileTreeExpanded) {
        collapseTree()
      }
    },
    [collapseTree, isMobileTreeExpanded, isSingleFileMode, updateSelectedPathDigest],
  )

  const FilterFilterComponent = useMemo(() => {
    return (
      <FileFilter
        basePath={pullRequest.pathName}
        fileFilterMenuOptions={fileFilter.menuOptions}
        fileFilterState={diffFilterState}
        setFileFilterState={handleSetFilterState}
        viewerLogin={currentUser.login}
        filterSize={!isTreeExpanded ? 'small' : 'medium'}
      />
    )
  }, [
    currentUser.login,
    fileFilter.menuOptions,
    diffFilterState,
    isTreeExpanded,
    pullRequest.pathName,
    handleSetFilterState,
  ])

  const {aliveChannel, bannersData} = useRouteHeaderData()

  return (
    <>
      <GlobalCommands
        commands={{
          'commit-diff-view:collapse-expand-comments': handleSwapDiffViewCommentCollapsePreference,
        }}
      />
      <LivePullRequestFilesToolbar
        commits={commits}
        currentUserLogin={user.currentUserLogin}
        fileFilter={FilterFilterComponent}
        isFileTreeExpanded={isTreeExpanded}
        lastReviewOid={user.lastReviewOid}
        pageLimits={pageLimits}
        pullRequest={pullRequest}
        repository={repository}
        shouldShowViewedFilesCount={user.shouldShowViewedFilesCount}
        totalFilesCount={diffSummaryCount}
        treeToggleElement={treeToggleElement}
        bannersData={bannersData}
        urls={urls}
        user={user}
        aliveChannel={aliveChannel}
      />
      {isMobileTreeExpanded && (
        <Dialog
          onClose={() => collapseTree()}
          position={{narrow: 'fullscreen', regular: 'left', wide: 'left'}}
          title={`Files`}
          className="p-0"
        >
          <PullRequestFileTree
            fileFilter={FilterFilterComponent}
            filteredDiffs={filteredDiffSummaries}
            onFileSelected={handleFileTreeLinkClick}
          />
        </Dialog>
      )}
      <SplitPageLayout.Pane
        position="start"
        sticky
        offsetHeader="60px"
        aria-label="File tree"
        padding="none"
        className={clsx(
          splitPagePaneHidden ? styles.HiddenPane : styles.Pane,
          isMobileTreeExpanded && styles.MobileExpanded,
          isTreeExpanded && styles.TreeExpanded,
        )}
        divider={{regular: 'line', narrow: 'none'}}
        widthStorageKey="diff-tree-pane-width"
        resizable
      >
        <PullRequestFileTree
          className={treeStyles['sidebar']}
          fileFilter={FilterFilterComponent}
          filteredDiffs={filteredDiffSummaries}
          onFileSelected={handleFileTreeLinkClick}
        />
      </SplitPageLayout.Pane>
      <SplitPageLayout.Content
        as="div"
        width="full"
        hidden={splitPageContentHidden}
        padding="none"
        className={clsx(styles.Content, isTreeExpanded && styles.TreeExpanded)}
      >
        <PageLimitsBanner pageLimits={pageLimits} repository={repository} urls={urls} />
        {isSingleFileMode ? (
          <SingleFileModeDiffEntryNavigator
            basePath={pullRequest.pathName}
            contextLinesURL={contextLinesURL}
            currentUser={currentUser}
            filteredDiffSummaries={filteredDiffSummaries}
            headBranchName={pullRequest.headBranch}
            headRepoName={pullRequest.headRepositoryName}
            headRepoOwner={pullRequest.headRepositoryOwnerLogin}
            pullRequestGlobalRelayId={pullRequest.globalRelayId}
            repository={repository}
          />
        ) : (
          <>
            <PullRequestDiffsList
              basePath={pullRequest.pathName}
              contextLinesURL={contextLinesURL}
              currentUser={currentUser}
              headBranchName={pullRequest.headBranch}
              headRepoName={pullRequest.headRepositoryName}
              headRepoOwner={pullRequest.headRepositoryOwnerLogin}
              pullRequestGlobalRelayId={pullRequest.globalRelayId}
              filteredDiffSummaries={filteredDiffSummaries}
              repository={repository}
            />
          </>
        )}
      </SplitPageLayout.Content>
    </>
  )
}

function useViewerSettings(user: FilesRoutePayload['user']) {
  const userSplitPreference = useSplitViewPreference(user.viewSettings.splitPreference)
  const userWhitespaceParam = useWhitespacePreference(user.viewSettings.hideWhitespace)
  return useMemo(() => {
    return {
      hideWhitespace: userWhitespaceParam,
      splitPreference: userSplitPreference,
      lineSpacing: user.viewSettings.lineSpacing,
      commentsPreference: user.viewSettings.commentsPreference,
    }
  }, [user.viewSettings.commentsPreference, user.viewSettings.lineSpacing, userSplitPreference, userWhitespaceParam])
}

try{ FilesEntrypoint.displayName ||= 'FilesEntrypoint' } catch {}
try{ Files.displayName ||= 'Files' } catch {}
try{ FilesComponent.displayName ||= 'FilesComponent' } catch {}
try{ FilterFilterComponent.displayName ||= 'FilterFilterComponent' } catch {}